import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DataGrid } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { jobsApi } from '../services/api';
import JobStatusChip from '../components/JobStatusChip';

const DEFAULT_FORM = {
  name: '',
  job_type: 'realtime',
  itr_log_path: '',
  output_dir: '',
  rta_catalogue_path: '',
  pdf_copies_dir: '',
  model: 'gemini-2.5-flash-lite',
  workers: 4,
  force_reprocess: false,
  generate_report: true,
};

function JobsPage() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [formError, setFormError] = useState('');

  const { data, isLoading, refetch } = useQuery(
    'jobs',
    () => jobsApi.list().then((r) => r.data),
    { refetchInterval: 5000 }
  );

  const createMutation = useMutation((payload) => jobsApi.create(payload), {
    onSuccess: () => {
      queryClient.invalidateQueries('jobs');
      setOpen(false);
      setForm(DEFAULT_FORM);
      setFormError('');
    },
    onError: (err) => {
      setFormError(err.response?.data?.detail || JSON.stringify(err.response?.data));
    },
  });

  const columns = [
    { field: 'id', headerName: 'ID', width: 60 },
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'job_type', headerName: 'Type', width: 120 },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => <JobStatusChip status={params.value} />,
    },
    {
      field: 'progress_pct',
      headerName: 'Progress',
      width: 100,
      valueFormatter: (value) => (value != null ? `${value}%` : '—'),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 170,
      valueFormatter: (value) => value ? new Date(value).toLocaleString() : '—',
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Button size="small" onClick={() => navigate(`/jobs/${params.row.id}`)}>
          View
        </Button>
      ),
    },
  ];

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleCreate = () => {
    setFormError('');
    createMutation.mutate(form);
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            ITR Processing — Pipeline Jobs
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {user?.username}
          </Typography>
          <Tooltip title="Review Output">
            <IconButton color="inherit" onClick={() => navigate('/review')}>
              <AssignmentIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Progress Dashboard">
            <IconButton color="inherit" onClick={() => navigate('/dashboard')}>
              <DashboardIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Bundle Downloads">
            <IconButton color="inherit" onClick={() => navigate('/bundle')}>
              <CloudDownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Logout">
            <IconButton color="inherit" onClick={logout}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h5">Jobs</Typography>
          <Box>
            <Tooltip title="Refresh">
              <IconButton onClick={() => refetch()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
              sx={{ ml: 1 }}
            >
              New Job
            </Button>
          </Box>
        </Box>

        <DataGrid
          rows={data?.results || data || []}
          columns={columns}
          loading={isLoading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        />
      </Container>

      {/* Diálogo nuevo job */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Pipeline Job</DialogTitle>
        <Divider />
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <TextField
            margin="dense"
            name="name"
            label="Job name"
            fullWidth
            required
            value={form.name}
            onChange={handleFormChange}
          />
          <TextField
            margin="dense"
            name="itr_log_path"
            label="Path: ITR LOG.xlsx"
            fullWidth
            required
            value={form.itr_log_path}
            onChange={handleFormChange}
            helperText="Absolute path to the Excel log file"
          />
          <TextField
            margin="dense"
            name="output_dir"
            label="Output directory"
            fullWidth
            required
            value={form.output_dir}
            onChange={handleFormChange}
            helperText="Absolute path where results will be saved"
          />
          <TextField
            margin="dense"
            name="pdf_copies_dir"
            label="ITR_COPIES directory (optional)"
            fullWidth
            value={form.pdf_copies_dir}
            onChange={handleFormChange}
          />
          <TextField
            margin="dense"
            name="rta_catalogue_path"
            label="Path: RTA_CATALOGUE.xlsx (optional)"
            fullWidth
            value={form.rta_catalogue_path}
            onChange={handleFormChange}
          />
          <TextField
            margin="dense"
            name="model"
            label="Gemini model"
            fullWidth
            value={form.model}
            onChange={handleFormChange}
          />
          <TextField
            margin="dense"
            name="workers"
            label="Parallel workers"
            type="number"
            fullWidth
            value={form.workers}
            onChange={handleFormChange}
            inputProps={{ min: 1, max: 16 }}
          />
          <FormControlLabel
            control={
              <Switch
                name="force_reprocess"
                checked={form.force_reprocess}
                onChange={handleFormChange}
              />
            }
            label="Force reprocess (ignore cache)"
            sx={{ mt: 1 }}
          />
          <FormControlLabel
            control={
              <Switch
                name="generate_report"
                checked={form.generate_report}
                onChange={handleFormChange}
              />
            }
            label="Generate Excel report on completion"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={createMutation.isLoading}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default JobsPage;
